import { Datum } from "../../../lib/csvUtils";
import { Tree, TreeNode } from "../../../lib/tree";
import { isEqual, sortBy } from "lodash";

const mapScheme = (code?: string) => {
  switch (code) {
    case "P":
      return "PROPRIETARY";
    case "M":
      return "MASTERCARD";
    case "V":
      return "VISA";
    case "A":
      return "AMEX";
    case "D":
      return "DINERS";
    case "J":
      return "JCB";
    case "e":
    case "x":
    case "I":
      return "EFTPOS";
    case "U":
      return "UNIONPAY";
    case "DI":
      return "DISCOVER";
    case "MA":
      return "MAESTRO";
    default:
      return "";
  }
};

const mapCardAttribute = (code?: string) => {
  switch (code) {
    case "C":
      return "CREDIT";
    // case "R": return "RELOADABLE";
    // case "N": return "NON-RELOADABLE";
    case "I":
      return "INTERNATIONAL"; // Key added for custom managed international bins
    default:
      return "DEBIT";
  }
};

const toATMAccounts = (entry: Datum) => {
  const code = entry["POS Account Type"];

  switch (code) {
    case "0":
      return ["POS"];
    case "1":
      return ["Savings"];
    case "2":
      return ["Cheque"];
    case "3":
      return ["Credit"];
    case "4":
      return ["Savings", "Cheque"];
    case "5":
      return ["Savings", "Credit"];
    case "6":
      return ["Cheque", "Credit"];
    case "7":
      return ["Savings", "Cheque", "Credit"];
    default:
      return [];
  }
};

const toCardType = (entry: Datum) => {
  const scheme = mapScheme(entry["Scheme (Primary)"]);
  const isCustom = entry["IS CUSTOM"] // Key added for custom managed international bins
  if(scheme === "VISA" || scheme === "MASTERCARD" || isCustom) {
    const cardAttribute = mapCardAttribute(entry["Card Attribute"]);
    return `${scheme} ${cardAttribute}`;
  } else {
    return scheme;
  }
};

const toLongDescription = (entry: Datum) => entry["Card Issuer Name"];

const toPosRoutingDestination = (entry: Datum) => entry["POS Routing Destination (part-1)"];

const addCardBin = (
  existingBins: BinSchema[],
  binFrom: string,
  binTo: string,
  length: number,
  account: string[],
  cardType: string
) => {
  const binFromNumber = Number.parseInt(binFrom);
  const binToNumber = Number.parseInt(binTo);
  let included = false;

  existingBins.forEach(binRange => {
    if (
      !included &&
      binRange.Length === length &&
      binRange.CardType === cardType &&
      isEqual(binRange.Account, account)
    ) {
      const from = Number.parseInt(binRange.From);
      const to = Number.parseInt(binRange.To);
      if (binFromNumber >= from && binToNumber <= to) {
        included = true;
      } else if (binFromNumber + 1 == from) {
        binRange.From = String(binFromNumber);
        included = true;
      } else if (binToNumber - 1 == to) {
        binRange.To = String(binToNumber);
        included = true;
      }
    }
  });

  if (!included) {
    existingBins.push({
      From: binFrom,
      To: binTo,
      Length: length,
      Account: account,
      CardType: cardType
    });
  }
  return existingBins;
};

interface BinSchema {
  From: string;
  To: string;
  Length: number;
  Account: string[];
  CardType: string;
}

interface CardBin {
  DescLong: string;
  DescShort: string;
  EarlyEMV: boolean;
  MagSwipeCCV: boolean;
  RefundOffline: boolean;
  DUKPT: boolean;
  PINBlock: string;
  Bin: BinSchema[];
}

interface CardTableEntry {
  id?: string | null;
  type: string;
  attributes: {
    Name: string;
    CardBin: CardBin[];
  };
}

class Mapper {
  private tree: Tree;
  private name: string;

  constructor(name: string) {
    this.name = name;
    this.tree = new Tree();
  }

  accept(entry: Datum) {
    const posRoutingDestination = toPosRoutingDestination(entry)

    if (posRoutingDestination === "ATM only BIN") return

    const longDescription = toLongDescription(entry);
    let longDescNode = this.tree.root.children?.find(
      childNode => childNode.data === longDescription
    );
    if (!longDescNode) {
      longDescNode = new TreeNode<string>(longDescription);
      this.tree.root.addChildNode(longDescNode);
    }

    const cardType = toCardType(entry);
    let cardTypeNode = longDescNode.children?.find(
      childNode => childNode.data.DescShort === cardType
    );
    if (!cardTypeNode) {
      cardTypeNode = new TreeNode({
        DescShort: cardType,
        EarlyEMV: false,
        MagSwipeCCV: false,
        RefundOffline: mapScheme(entry["Scheme (Primary)"]) === "AMEX",
        Bin: []
      });
      longDescNode.addChildNode(cardTypeNode);
    }
    const cardBin = addCardBin(
      cardTypeNode.data.Bin,
      entry["BIN FROM"] || entry["BIN"] as string,
      entry["BIN TO"] || entry["BIN"] as string,
      Number.parseInt(entry["PAN Length"] as string),
      toATMAccounts(entry),
      toCardType(entry)
    );
    cardTypeNode.data.Bin = cardBin;
  }

  collect(DUKPT: boolean, PINBlock: string) {
    const result: CardTableEntry = {
      type: "Card",
      attributes: {
        Name: this.name,
        CardBin: []
      }
    };
    this.tree.visitDepthFirst(
      (node, context: { issuer: string }) => {
        if (node.isLeaf()) {
          const cardBin = node.data;
          cardBin.PINBlock = PINBlock;
          cardBin.DUKPT = DUKPT;
          cardBin.DescLong = context.issuer;
          cardBin.Bin = sortBy(cardBin.Bin, [
            binEntry => binEntry.To - binEntry.From,
            "From"
          ]);
          cardBin.Bin = cardBin.Bin.sort((binEntryA, binEntryB) => {
            return binEntryB.To.length - binEntryA.To.length
          });
          result.attributes.CardBin.push(cardBin);
        } else {
          context.issuer = node.data;
        }
        return true;
      },
      { issuer: null }
    );

    return result;
  }
}

export default Mapper;
